import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import i18n from 'i18n';
import _ from 'lodash';
import { OPERATE } from 'enum/Operate';
import { Limitation, LimitationData } from 'core/limitation/Limitation';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { useCallAPI } from 'hooks/useCallAPI';
import { CookieUserDefaults, UserDefaults } from 'utils/UserDefaults';
import {
  Device,
  DeviceDataContext,
  DeviceMap,
  SpaceAttribute,
  SpaceAttributeMap,
  SpaceAttributes,
  SpaceDetailMap
} from './DeviceDataContext';
import { DeviceUnionFind } from 'core/googleMaps/DeviceUnionFind';
import { googleMapsUsageTracker } from 'core/googleMapsUsage/GoogleMapsUsageTracker';
import { FilterItemComponent } from './FilterItemComponent';
import { MapContainer } from './MapContainer';
import styles from './outdoorMapSection.module.scss';
import { FormikField } from 'components/common/form/field/FormikField';

const cookieUserDefaults: UserDefaults = new CookieUserDefaults();

export type OutdoorMapSectionProps = {
  limitationValue: LimitationData;
  onLimitationChange: (
    operate: string,
    type: string,
    value: SelectOptions[]
  ) => void;
  spaceAttributes: SpaceAttributes;
  spaceAttributeMap: SpaceAttributeMap;
  deviceMap: DeviceMap;
  spaceDetailMap: SpaceDetailMap;
  spaceChannelId: number;
  fetchMaps: () => Promise<void>;
  mapsApiKey: string;
  mapDeviceType: string;
  deviceUnionFind?: DeviceUnionFind;
  /** ECOPD-5615: 走期內已被 10 個活動綁滿的機台（值＝device option value）。 */
  overBindingLimitValues?: Set<string>;
};

/**
 * Resolve devices from a limitation array using spaceAttributeMap.
 * Within same attributeType: UNION
 * Between SSP attributeTypes: INTERSECTION
 * Between non-SSP attributeTypes: UNION
 */
function resolveDevicesFromLimitations(
  limitations: Limitation[],
  spaceAttributeMap: SpaceAttributeMap,
  spaceAttributes: SpaceAttributes,
  deviceMap: DeviceMap,
  mapDeviceType: string
): Device[] {
  const limitationValuesObj = _.keyBy(limitations, 'type');
  let spaceAttributeDevices: Device[] = [];
  let regularDevices: Device[] = [];
  // Distinguish "no SSP iterated yet" from "SSP iterated and produced empty".
  // `_.isEmpty(spaceAttributeDevices)` alone can't tell them apart, so a prior
  // intersection that legitimately collapsed to [] used to be wiped by the
  // next SSP's REPLACE branch — restarting the chain and breaking AND-of-SSPs.
  let hasIntersectedSSP = false;

  for (const attributeType of _.keys(spaceAttributeMap)) {
    if (!limitationValuesObj[attributeType]) continue;
    const limitationValue = limitationValuesObj[attributeType].value;
    if (!Array.isArray(limitationValue)) continue;

    let attributeDevices: Device[] = [];
    for (const attr of limitationValue) {
      const devices = _.get(spaceAttributeMap, [attributeType, attr.value], []);
      attributeDevices = _.unionBy(attributeDevices, devices, 'value');
    }

    if (spaceAttributes[attributeType]) {
      spaceAttributeDevices = hasIntersectedSSP
        ? _.intersectionBy(spaceAttributeDevices, attributeDevices, 'value')
        : [...attributeDevices];
      hasIntersectedSSP = true;
    } else {
      regularDevices = _.isEmpty(regularDevices)
        ? [...attributeDevices]
        : _.unionBy(regularDevices, attributeDevices, 'value');
    }
  }

  // Directly-selected devices from map clicks.
  // When mapDeviceType IS in spaceAttributeMap (e.g. storeNum, simulcastGroup), the attribute
  // loop above only resolves GROUP-level keys (e.g. '台北市' → [stores]). After explosion,
  // individual device IDs are written to mapDeviceType and are NOT valid spaceAttributeMap keys,
  // so the loop returns nothing for them. Add only those non-resolved values directly here;
  // values that already exist as keys of `spaceAttributeMap[mapDeviceType]` (DEVICE-mode's
  // `deviceCharacteristic`, where individual triples are also bag-keys) are intentionally
  // skipped — they've gone through the SSP-INTERSECT branch above, and re-unioning them
  // would bypass the intersection with other SSP attributes (e.g. outdoorPlaceCategories).
  const mapDeviceLimitation = limitationValuesObj[mapDeviceType];
  if (mapDeviceLimitation && Array.isArray(mapDeviceLimitation.value)) {
    const mapDeviceAttrMap = spaceAttributeMap[mapDeviceType] ?? {};
    const directDevices = (mapDeviceLimitation.value as Device[]).filter(
      (d) => !mapDeviceAttrMap[String(d.value)]
    );
    regularDevices = _.unionBy(regularDevices, directDevices, 'value');
  }

  return _.unionBy(spaceAttributeDevices, regularDevices, 'value').filter(
    (d) => !!deviceMap[d.value]
  );
}

export const OutdoorMapSection: React.FC<OutdoorMapSectionProps> = ({
  limitationValue,
  onLimitationChange,
  spaceAttributes,
  spaceAttributeMap,
  deviceMap,
  spaceDetailMap,
  spaceChannelId,
  fetchMaps,
  mapsApiKey,
  mapDeviceType,
  deviceUnionFind,
  overBindingLimitValues
}) => {
  const language = cookieUserDefaults.language;
  const [showMap, setShowMap] = useState<boolean>(false);
  const [filterMap, setFilterMap] = useState<{
    [attributeType: string]: {
      [spaceAttribute: string]: SpaceAttribute;
    };
  }>({});
  const { loading, callAPIs } = useCallAPI();

  useEffect(() => {
    if (!showMap) return;
    callAPIs([fetchMaps]);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowMap(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Flush buffered Google Maps usage events when the map
      // overlay closes (Switch-to-List, Escape, or an unmount of this
      // section). Best-effort; the tracker guards against concurrent flushes.
      void googleMapsUsageTracker.flush();
    };
  }, [showMap, fetchMaps, callAPIs]);

  const resolveDevices = useCallback(
    (limitations: Limitation[]) =>
      resolveDevicesFromLimitations(
        limitations,
        spaceAttributeMap,
        spaceAttributes,
        deviceMap,
        mapDeviceType
      ),
    [spaceAttributeMap, spaceAttributes, deviceMap, mapDeviceType]
  );

  // Compute selectedDevices = INCLUDE devices - EXCLUDE devices
  // No useMemo: limitationValue is mutated in place by EditLimitation.handleChange
  // (same reference), so memo deps would never detect the change. Since this
  // component only re-renders when the model fires events, computing inline is safe.
  const selectedDevices: Device[] = _.differenceBy(
    resolveDevices(_.defaultTo(limitationValue.include, [])),
    resolveDevices(_.defaultTo(limitationValue.exclude, [])),
    'value'
  );

  // All devices that can be shown on the map
  const devicesToShow: Device[] = useMemo(() => {
    const allDevices: Device[] = [];
    for (const attributeType of _.keys(spaceAttributeMap)) {
      for (const attrValue of _.keys(spaceAttributeMap[attributeType])) {
        const devices = spaceAttributeMap[attributeType][attrValue];
        for (const device of devices) {
          if (deviceMap[device.value]) {
            allDevices.push(device);
          }
        }
      }
    }
    return _.uniqBy(allDevices, 'value');
  }, [spaceAttributeMap, deviceMap]);

  // Devices highlighted by the active filter selection
  const filteredDevices: Device[] = useMemo(() => {
    let result: Device[] = [];
    _.forEach(_.keys(filterMap), (attributeType: string) => {
      const filteredSpaceAttributes = _.keys(filterMap[attributeType]);
      _.forEach(filteredSpaceAttributes, (spaceAttribute: string) => {
        const devices: Device[] = _.get(
          spaceAttributeMap,
          [attributeType, spaceAttribute],
          []
        );
        result = _.unionBy(result, devices, 'value');
      });
    });
    return result;
  }, [filterMap, spaceAttributeMap]);

  const handleFilterCallback = useCallback(
    (attributeType: string, spaceAttribute: SpaceAttribute) => {
      setFilterMap((prev) => {
        const prevSpaceAttributeMap = _.defaultTo(prev[attributeType], {});
        let newSpaceAttributeMap = prevSpaceAttributeMap[spaceAttribute.value]
          ? _.omit(prevSpaceAttributeMap, spaceAttribute.value)
          : {
              ...prevSpaceAttributeMap,
              [spaceAttribute.value]: spaceAttribute
            };
        if (!_.isEmpty(spaceAttribute.options)) {
          _.each(spaceAttribute.options, (option: SelectOptions) => {
            newSpaceAttributeMap[spaceAttribute.value]
              ? (newSpaceAttributeMap[option.value] = option)
              : (newSpaceAttributeMap = _.omit(
                  newSpaceAttributeMap,
                  option.value
                ));
          });
        }
        _.values(spaceAttributes[attributeType].options).forEach(
          (attribute) => {
            if (!_.isEmpty(attribute.options)) {
              const allSubOptionsSelected = _.every(
                attribute.options,
                (option: SelectOptions) => newSpaceAttributeMap[option.value]
              );
              allSubOptionsSelected
                ? (newSpaceAttributeMap[attribute.value] = attribute)
                : (newSpaceAttributeMap = _.omit(
                    newSpaceAttributeMap,
                    attribute.value
                  ));
            }
          }
        );
        return {
          ...prev,
          [attributeType]: newSpaceAttributeMap
        };
      });
    },
    [spaceAttributes]
  );

  /**
   * Explode grouped limitations that contain any of the given devices.
   * For each attribute-type limitation in the given operate that overlaps with
   * targetDeviceValues:
   *   - Attrs with NO overlap stay in the original attribute type as-is.
   *   - Attrs WITH overlap are decomposed: the remaining devices (minus target)
   *     are written into mapDeviceType, not back into the attribute type.
   */
  const explodeGroupsForDevices = useCallback(
    (operate: string, targetDeviceValues: Set<string | number>) => {
      // Snapshot to avoid iterating a mutating array
      const limitations = [
        ..._.defaultTo(limitationValue[operate], [])
      ] as Limitation[];
      for (const limitation of limitations) {
        if (!Array.isArray(limitation.value)) continue;
        const attributeType = limitation.type;
        const attrMap = spaceAttributeMap[attributeType];
        if (!attrMap) continue;

        const keepAttrs: SelectOptions[] = [];
        const remainingDevices: Device[] = [];
        let needsExplosion = false;

        for (const attr of limitation.value) {
          const devicesInGroup = _.get(attrMap, attr.value, []) as Device[];
          const overlaps = devicesInGroup.some((d) =>
            targetDeviceValues.has(d.value)
          );
          if (overlaps) {
            needsExplosion = true;
            // Decompose: move non-target devices to mapDeviceType
            remainingDevices.push(
              ...devicesInGroup.filter((d) => !targetDeviceValues.has(d.value))
            );
          } else {
            // No overlap: keep this attr in its original type
            keepAttrs.push(attr);
          }
        }

        if (needsExplosion) {
          // Update original attribute limitation (may be empty → removes it)
          onLimitationChange(operate, attributeType, keepAttrs);
          // Merge remaining devices into mapDeviceType for this operate
          if (remainingDevices.length > 0) {
            const currentLimitations = _.defaultTo(
              limitationValue[operate],
              []
            ) as Limitation[];
            const existingCustom = currentLimitations.find(
              (l) => l.type === mapDeviceType
            );
            const existingValues: SelectOptions[] =
              existingCustom && Array.isArray(existingCustom.value)
                ? (existingCustom.value as SelectOptions[])
                : [];
            onLimitationChange(
              operate,
              mapDeviceType,
              _.uniqBy([...existingValues, ...remainingDevices], 'value')
            );
          }
        }
      }
    },
    [limitationValue, spaceAttributeMap, onLimitationChange, mapDeviceType]
  );

  /**
   * Shared preparation for map add/remove handlers:
   * 1. Snapshot INCLUDE before explosions (preserves intersection semantics)
   * 2. Explode overlapping INCLUDE groups (and optionally EXCLUDE groups)
   * 3. Compute stillExcludedSet from post-explosion EXCLUDE state
   */
  const prepareExplosion = useCallback(
    (devices: SelectOptions[], explodeExclude: boolean) => {
      const deviceValues = new Set(devices.map((d) => d.value));
      const preExplosionInclude = resolveDevices(
        _.defaultTo(limitationValue.include, [])
      );
      explodeGroupsForDevices(OPERATE.INCLUDE, deviceValues);
      if (explodeExclude) {
        explodeGroupsForDevices(OPERATE.EXCLUDE, deviceValues);
      }
      const stillExcludedSet = new Set(
        resolveDevices(_.defaultTo(limitationValue.exclude, [])).map(
          (d) => d.value
        )
      );
      return { deviceValues, preExplosionInclude, stillExcludedSet };
    },
    [resolveDevices, limitationValue, explodeGroupsForDevices]
  );

  const handleClickMapDevices = useCallback(
    (devices: SelectOptions[]) => {
      const { preExplosionInclude, stillExcludedSet } = prepareExplosion(
        devices,
        true
      );
      const filteredInclude = preExplosionInclude.filter(
        (d) => !stillExcludedSet.has(d.value)
      );
      onLimitationChange(
        OPERATE.INCLUDE,
        mapDeviceType,
        _.unionBy(filteredInclude, devices, 'value')
      );
    },
    [prepareExplosion, onLimitationChange, mapDeviceType]
  );

  const handleRemoveMapDevices = useCallback(
    (devices: SelectOptions[]) => {
      const { deviceValues, preExplosionInclude, stillExcludedSet } =
        prepareExplosion(devices, false);
      onLimitationChange(
        OPERATE.INCLUDE,
        mapDeviceType,
        preExplosionInclude.filter(
          (d) => !deviceValues.has(d.value) && !stillExcludedSet.has(d.value)
        )
      );
    },
    [prepareExplosion, onLimitationChange, mapDeviceType]
  );

  return (
    <DeviceDataContext.Provider
      value={{
        filter: { device: filteredDevices },
        deviceMap,
        spaceDetailMap,
        spaceChannelId,
        mapsApiKey,
        language,
        deviceUnionFind,
        overBindingLimitValues
      }}
    >
      {/* Toggle button — shown at bottom-right when map is hidden */}
      {!showMap && (
        <FormikField.Custom label=''>
          <Button
            variant='secondary'
            size='sm'
            onClick={_.partial(setShowMap, true)}
          >
            {i18n.t('customOutdoorDevice.buttons.switchToMap')}
          </Button>
        </FormikField.Custom>
      )}

      {/* Full-screen map overlay */}
      {showMap && (
        <div className={styles.mapOverlay}>
          {loading && <LoadingIndicator />}
          <div className={styles.mapHeader}>
            <div className={styles.filterContainer}>
              {_.keys(spaceAttributes).map((attributeType) => (
                <FilterItemComponent
                  key={attributeType}
                  attributeType={attributeType}
                  title={_.defaultTo(spaceAttributes[attributeType].title, '')}
                  options={_.defaultTo(
                    spaceAttributes[attributeType].options,
                    []
                  )}
                  filterSpaceAttributeMap={_.defaultTo(
                    filterMap[attributeType],
                    {}
                  )}
                  onFilterCallback={handleFilterCallback}
                />
              ))}
            </div>
          </div>
          <div className={styles.mapBody}>
            <MapContainer
              selectedDevices={selectedDevices}
              devicesToShow={devicesToShow}
              onClickDevices={handleClickMapDevices}
              onRemoveDevices={handleRemoveMapDevices}
            />
            <Button
              className={styles.switchToListButton}
              variant='secondary'
              size='sm'
              onClick={_.partial(setShowMap, false)}
            >
              {i18n.t('customOutdoorDevice.buttons.switchToList')}
            </Button>
          </div>
        </div>
      )}
    </DeviceDataContext.Provider>
  );
};
